import { Button, Card, CardContent, FormControlLabel, Radio, RadioGroup, TextField, Typography } from "@mui/material"
import { useFormik } from "formik"
import { useEffect, useMemo, useState } from "react"
import { upsertProject } from "../services/project.service"
import { useAppDispatch } from "../store/hooks"
import { toggleAlert, toggleSpinner } from "../store/resuders/app.reducer"
import { useTranslation } from 'react-i18next';
import { CHANNELS } from "../types/global.enum"
import { getTestCaseList } from "../services/testcase.service"

export const AddProject = ({ data = null as any, onDone = null as any }) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [hasExistingTestCases, setHasExistingTestCases] = useState(false);

    const isRenameFlow = useMemo(() => Boolean((data as any)?.isRename), [data]);
    const formik = useFormik({
        initialValues: {
            channelType: CHANNELS.VOICE,
            name: '',
            testProjectID: undefined,
            remark: '',
        },
        onSubmit: async (values: any) => {
            dispatch(toggleSpinner(true));
            const result = await upsertProject(values);
            dispatch(toggleSpinner(false));
            if (["ERR_BAD_RESPONSE", "ERR_BAD_REQUEST"].includes(result?.code)) {
                dispatch(toggleAlert(result?.response?.data?.data?.messages?.join(',')))
            } else {
                const createdProject = result?.data ?? result;
                onDone && onDone(createdProject);
            }
        }
    })

    const confirm = () => {
        formik.submitForm();
    }

    useEffect(() => {
        let isMounted = true;

        const setFormikValues = () => {
            if (!data) return;

            formik.setValues({
                channelType: data.channelType || CHANNELS.VOICE,
                name: data.name || '',
                testProjectID: data.testProjectID,
                remark: data.remark || '',
            });
        };

        const checkProjectHasCases = async () => {
            // If the project already includes test cases from API response, short-circuit
            const embeddedCases = Array.isArray((data as any)?.testCases) ? (data as any).testCases : [];
            if (embeddedCases.length > 0) {
                setHasExistingTestCases(true);
                return;
            }

            if (!data?.testProjectID || !data?.channelType) {
                setHasExistingTestCases(false);
                return;
            }

            try {
                const caseList = await getTestCaseList(data.testProjectID, data.channelType as CHANNELS, 0, 1);
                const content = caseList?.data?.content ?? caseList?.content ?? [];
                if (isMounted) {
                    setHasExistingTestCases(Array.isArray(content) ? content.length > 0 : false);
                }
            } catch (error) {
                console.error('Failed to determine if project has test cases', error);
                if (isMounted) {
                    setHasExistingTestCases(false);
                }
            }
        };

        setFormikValues();
        if (data) {
            checkProjectHasCases();
        } else {
            setHasExistingTestCases(false);
        }

        return () => {
            isMounted = false;
        };
    }, [data]);

    const disableChannelSelection = hasExistingTestCases || isRenameFlow;

    return (
        <Card className="h-full">
            <CardContent>
                <form onSubmit={formik.handleSubmit}>
                    <div className="form-header">
                        <Typography variant="subtitle1">{data ? t('Edit') : 'Adding a'} {t('Project')}</Typography>
                    </div>
                    <div className="pt-32 pl-40 pr-40">
                        <RadioGroup id="channelType" name="channelType" row style={{ marginLeft: 10 }} onChange={formik.handleChange} value={formik.values?.channelType}>
                            <FormControlLabel value={CHANNELS.VOICE} control={<Radio color="default" />} label={t("Voice")} disabled={disableChannelSelection} />
                            <FormControlLabel  value={CHANNELS.DIGITAL} className="ml-80" control={<Radio color="default" />} label={t("Digital")} disabled={disableChannelSelection} />
                        </RadioGroup>
                        <div className="mt-22">
                            <TextField id="name" name="name" onChange={formik.handleChange} fullWidth label={t("Project Name")} value={formik.values?.name} />
                        </div>
                    </div>
                    <div className="db center mt-54">
                        <Button variant="contained" onClick={confirm} color="primary">{t('Confirm')}</Button>
                    </div>
                </form>
            </CardContent>
        </Card>
    )
}